import { useEffect, useState, useCallback, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLoginMutation } from '@api/baseApi';
import { setPublicAuthToken, findPublicAuthToken } from '@features/authentication/storageFunctions';
import { LoginMode } from '@features/authentication/types/LoginMode';
import { AppAuthMode } from '@features/authentication/types/AppAuthMode';
import { setAppAuthMode } from '@features/authentication/store/slice';



export const useLoginPage = () => {
	const { t } = useTranslation('forms');

	const [ loginMode, setLoginMode ] = useState(LoginMode.checkToken);
	const [ loginAttempts, setLoginAttempts ] = useState(0);

	const dispatch = useDispatch();
	const changeAppAuthMode = (mode: AppAuthMode) => dispatch(setAppAuthMode(mode));
	const [ runLogin, { isLoading } ] = useLoginMutation();


	useEffect(() => {
		let token = findPublicAuthToken();
		if (!token) {
			setLoginMode(LoginMode.loginRequired);
		}
	}, []);


	useEffect(() => {
		if (loginAttempts > 5) {
			changeAppAuthMode(AppAuthMode.authFailed);
		}
	}, [ loginAttempts ]);

	
	const handleLogin = useCallback(async (username: string, password: string) => {
		try {
			let response = await runLogin({ username, password });

			if ('data' in response && response.data) {
				setPublicAuthToken(response.data);
				return;
			}

			if ('error' in response && 'status' in response.error && response.error.status === 401) {
				setLoginAttempts(loginAttempts + 1)
				setLoginMode(LoginMode.loginFailed);
				return;
			}

			changeAppAuthMode(AppAuthMode.authFailed);
		}
		catch (error) {
			changeAppAuthMode(AppAuthMode.authFailed);
		}
	}, [ loginAttempts ]);


	const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		let data = Object.fromEntries(new FormData(event.currentTarget));
		let username = data['username'] as string;
		let password = data['password'] as string;

		if (username && password) {
			handleLogin(username, password);
		}
	},[ handleLogin ]);


	const validateEmail = useCallback((value: string): string | true => {
		if (!value || value.length === 0) return t('validation.requiredField');
		if (!/^\S+@\S+\.\w+$/.test(value)) return t('validation.incorrectEmailFormat');
		return true;
	},[]);


	return [ loginMode, isLoading, handleSubmit, validateEmail ] as const;
}